import { memo, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { AddChatIcon } from "@/assets/ai-assistent/add-chat-icon.tsx"
import { ChatsIcon } from "@/assets/ai-assistent/chats-icon.tsx"
import Skeleton from "@/components/skeleton/skeleton.tsx"
import { useGetSuggestedQuestionsQuery } from "@/features/assistant/api/suggested-questions-api.ts"
import { type SuggestedQuestion } from "@/features/assistant/types/assistant.ts"

interface Props {
  onQuestionClick?: (question: SuggestedQuestion) => void
}

const QuestionItem = memo(
  ({
    question,
    onClick,
  }: {
    question: SuggestedQuestion
    onClick?: (question: SuggestedQuestion) => void
  }) => {
    const { t } = useTranslation()

    return (
      <div
        onClick={() => onClick?.(question)}
        className="relative flex min-h-[70px] w-full flex-col items-center justify-start gap-1 rounded-sm rounded-br-[0] border border-brown-500 bg-white p-3"
      >
        <div className="relative z-10 grid w-full grid-cols-[auto,auto] items-start justify-start gap-2">
          <ChatsIcon className="mt-[2px] h-5 w-5 text-zinc-500" />
          <p className="text-base text-zinc-600">{t(question.question_text)}</p>
        </div>
      </div>
    )
  },
)

QuestionItem.displayName = "QuestionItem"

const SuggestedQuestions = memo(({ onQuestionClick }: Props) => {
  const { data: questions, isLoading } = useGetSuggestedQuestionsQuery()
  const { t } = useTranslation()

  const questionsList = useMemo(() => {
    if (!questions?.length || isLoading) {
      return Array.from({ length: 4 }).map((_, index) => (
        <Skeleton
          className="relative flex min-h-[70px] w-full flex-col items-center justify-start gap-1 rounded-sm rounded-br-[0] border border-brown-500 bg-white p-3"
          key={index}
        />
      ))
    }

    return questions.map((question, index) => (
      <QuestionItem
        key={`${question}-${index}`}
        question={question}
        onClick={onQuestionClick}
      />
    ))
  }, [questions, isLoading, onQuestionClick])

  if (!questions?.length) return null

  return (
    <div className="mt-6 flex w-full flex-col items-center gap-3">
      <div className="flex w-full items-center justify-start gap-1">
        <AddChatIcon className="h-5 w-5 text-brown-500" />
        <p className="text-zinc-600">{t("suggestedQuestions")}</p>
      </div>
      {questionsList}
    </div>
  )
})

SuggestedQuestions.displayName = "SuggestedQuestions"

export default SuggestedQuestions
